import React, { useEffect, useState } from 'react';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import useFetch from 'use-http';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Invoice from 'src/models/leasing/Invoice';
import InvoiceCard from '../components/InvoiceCard.redesign';
import AddInvoiceDialog from '../components/AddInvoiceDialog';
import DialogList from 'src/dialogs';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import actions from 'src/actions';
import moment from 'moment';
import InvoiceableData from 'src/models/leasing/InvoiceableData';
import RHFTextArea from 'src/components/controls/tempRHFControls/RHFTextArea';
import { useForm } from 'react-hook-form';
import LeaseNotes from 'src/models/leasing/LeaseNotes';
import CreateCheckDialog from '../components/CreateCheckDialog';
import Check from 'src/models/Check';
import ManageChecksDialog from '../components/ManageChecksDialog';
import CheckOptions from 'src/models/CheckOptions';
import ManageInvoiceGroupsDialog from '../components/ManageInvoiceGroupsDialog';
import InvoiceGroup from 'src/models/leasing/InvoiceGroup';
import ExtendCheckDueDateDialog from '../components/ExtendCheckDueDateDialog';
import Card from 'src/components/common/Card.redesign';
import CardContainer from 'src/components/common/CardContainer.redesign';
import MathUtil from 'src/util/MathUtil';

// export interface IInvoicingDetailProps {
//     contractNumber?: number;
// }

const InvoicingDetail = () => {
    const { get, put, response } = useFetch();
    const [invoiceData, setInvoiceData] = useState<InvoiceableData>(undefined);
    const [currentInvoice, setCurrentInvoice] = useState<Invoice>(undefined);
    const [currentCheck, setCurrentCheck] = useState<Check>(undefined);
    const [checkOptions, setCheckOptions] = useState<CheckOptions>(undefined);
    const [checksList, setChecksList] = useState<Check[]>([]);
    const [invoiceGroupsList, setInvoiceGroupsList] = useState<InvoiceGroup[]>([]);
    const { register, handleSubmit, reset, errors } = useForm();
    const dispatch = useDispatch();

    const pathArray = window.location.pathname.split('/');
    const contractNumber = parseInt(pathArray[pathArray.length - 2], 10);

    useEffect(() => {
        getInvoiceData();
        console.log('I have loaded');
    }, []);

    useEffect(() => {
        reset(invoiceData?.leaseNotes);
        loadCheckData();
        loadInvoiceGroupData();
    }, [invoiceData]);

    const getInvoiceData = async () => {
        let result: InvoiceableData = await get(`/v1.0/invoicing/${contractNumber}`);
        
        if (response.ok) {
            result = formatData(result);
            setInvoiceData(result);
            getCheckOptions();
        } else {
            console.log('Unable to retrieve Invoice Data');
        }
    };

    const getCheckOptions = async () => {
        let data: CheckOptions = await get(`v1.0/clients/${invoiceData?.clientId}/projects/${invoiceData?.projectId}/check/check-options`);

        if (response.ok) {
            setCheckOptions(data);
        } else {
            console.log('Unable to retrieve Check Options.');
        }
    };

    const navigateToContract = () => {
        let navLabel = invoiceData?.sectionNumber > 0 ? 'sections' : 'maps';
        let navNumber = invoiceData?.sectionNumber > 0 ? invoiceData.sectionNumber : invoiceData?.easternMapNumber > 0 ? invoiceData.easternMapNumber : 0;

        if (invoiceData && invoiceData.contractId > 0) {
            dispatch(push(`/clients/${invoiceData.clientNumber}/projects/${invoiceData.projectNumber}/${navLabel}/${navNumber}/tracts/${invoiceData.tractNumber}/horizons/${invoiceData.horizonNumber}/ownership/${invoiceData.ownershipId}/leases/${invoiceData.contractId}`));
        }
    };

    const onSubmit = async (leaseNotes: LeaseNotes) => {
        leaseNotes = formatNotes(leaseNotes);

        if (leaseNotes.id > 0) {
            await put(`v1.0/clients/${invoiceData.clientId}/projects/${invoiceData.projectNumber}/leases/notes/${leaseNotes.id}`, leaseNotes);

            if (response.ok) {
                getInvoiceData();
            }
        }
    };

    const convertToCurrency = (value: number): string => {
        if (value !== undefined) {
            let options = {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2
            };
    
            let dollarString = new Intl.NumberFormat( 'en-US', options);
            return dollarString.format(value);
        }
        return undefined;
    };

    const invoiceTotals = () => {
        if (invoiceData?.invoices?.length > 0) {
            var totals = {
                bankCharge: 0,
                bonusAmount: 0,
                rentAmount: 0,
                recordingFee: 0,
                otherFee: 0,
                invoiceTotal: 0
            };

            for (const c of invoiceData.invoices) {
                totals.bankCharge += c.bankCharge;
                totals.bonusAmount += c.bonusAmount;
                totals.rentAmount += c.rentAmount;
                totals.recordingFee += c.recordingFee;
                totals.otherFee += c.otherFee;
                totals.invoiceTotal += c.bankCharge + c.bonusAmount + c.rentAmount + c.recordingFee + c.otherFee;
            }

            return (
                <Card className="small-input" newContext={true}>
                    <div style={{ display: 'flex' }}>
                        <div className="mr-2" style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <div style={{ padding: '1rem', width: '15%' }}>
                                    <div style={{ fontSize: '1.6rem' }}>TOTALS:</div>
                                </div>
                                <div style={{ padding: '1rem', width: '15%' }}>
                                    <div>{convertToCurrency(totals.bonusAmount)}</div>
                                </div>
                                <div style={{ padding: '1rem', width: '15%' }}>
                                    <div>{convertToCurrency(totals.rentAmount)}</div>
                                </div>
                                <div style={{ padding: '1rem', width: '15%' }}>
                                    <div>{convertToCurrency(totals.recordingFee)}</div>
                                </div>
                                <div style={{ padding: '1rem', width: '15%' }}>
                                    <div>{convertToCurrency(totals.bankCharge)}</div>
                                </div>
                                <div style={{ padding: '1rem', width: '15%' }}>
                                    <div>{convertToCurrency(totals.otherFee)}</div>
                                </div>
                                <div style={{ padding: '1rem', width: '10%' }}>
                                    <div>{convertToCurrency(totals.invoiceTotal)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>
            );
        } else {
            return null;
        }
    };

    const displayInvoices = () => {
        if (invoiceData?.invoices?.length > 0) {
            return invoiceData.invoices.map((c: Invoice, i: number) => {
                c.invoiceTotal = c.bankCharge + c.bonusAmount + c.rentAmount + c.recordingFee + c.otherFee;

                return (
                    <div key={i}>
                        <InvoiceCard
                            invoice={c}
                            index={i}
                            onInvoiceAction={(action: string) => onInvoiceAction(action, i)}
                            convertToCurrency={(value: number) => convertToCurrency(value)}
                        />
                    </div>
                );
            });
        } else {
            return <div>No Invoices</div>;
        }
    };

    const hideDialogBox = (isReset: boolean) => {
        if (isReset) {
            getInvoiceData();
            loadCheckData();
        }
        dispatch(actions.layout.hideDialog());
    };

    const onInvoiceAction = (action: string, i: number) => {
        let currentId;

        if (i >= 0) {
            let invoice = invoiceData.invoices[i];
            currentId = invoice.id;
            invoice.id = currentId;
            setCurrentInvoice(invoice);
        } else {
            setCurrentInvoice(undefined);
        }

        switch (action) {
            case 'Manage Invoice':
                onUpdateInvoice();
                break;
            case 'Create Check':
                onCreateCheck();
                break;
            case 'Manage Checks':
                onManageChecks();
                break;
            case 'Manage Invoice Groups':
                onManageInvoiceGroups();
                break;
            case 'Post Invoice':
                onUpdateInvoiceStatus('Posted', currentId);
                break;
            case 'Void Invoice':
                onUpdateInvoiceStatus('Void', currentId);
                break;
            default:
                break;
        }
    };

    const loadCheckData = async () => {
        if (!!invoiceData?.contractId && invoiceData.contractId > 0) {
            let data: Check[] = await get(`v1.0/clients/${invoiceData.clientId}/projects/${invoiceData.projectId}/check/by-contract/${invoiceData.contractId}`);
            data.forEach((ch, key) => {
                ch = formatCheckData(ch);
                data[key] = ch;
            });

            // Load Check Data;
            if (response.ok) {
                setChecksList(data);
            }
        }
    };

    const loadInvoiceGroupData = async () => {
        if (!!invoiceData?.clientId && invoiceData.clientId > 0) {
            let data: InvoiceGroup[] = await get(`v1.0/clients/${invoiceData.clientId}/projects/${invoiceData.projectId}/invoice/invoice-group`);
            data.forEach((ig, key) => {
                ig = formatInvoiceGroupData(ig);
                data[key] = ig;
            });

            if (response.ok) {
                setInvoiceGroupsList(data);
            }
        }
    };

    const extendDueDate = (checkNumber: string) => {
        let checkToUpdate = checksList.indexOf(checksList.find(cl => cl.checkNumber === checkNumber));
        setCurrentCheck(checksList[checkToUpdate]);
        dispatch(actions.layout.showDialog(DialogList.EXTEND_CHECK_DUE_DATE));
    };

    const onUpdateInvoice = () => {
        dispatch(actions.layout.showDialog(DialogList.ADD_INVOICE));
    };

    const onCreateCheck = () => {
        dispatch(actions.layout.showDialog(DialogList.CREATE_CHECK));
    };

    const onManageChecks = () => {
        dispatch(actions.layout.showDialog(DialogList.MANAGE_CHECKS));
    };

    const onManageInvoiceGroups = () => {
        dispatch(actions.layout.showDialog(DialogList.MANAGE_INVOICE_GROUPS));
    };

    const onUpdateInvoiceStatus = async (newStatus: string, currentId: number) => {
        console.log('Updating new status', newStatus);
        console.log('Updating Invoice ', currentId);

        if (currentId > 0) {
            await put(`v1.0/clients/${invoiceData.clientId}/projects/${invoiceData.projectNumber}/invoice/${currentId}/invoice-status/${newStatus}`);

            if (response.ok) {
                getInvoiceData();
            }
        }
    };

    const formatData = (data: InvoiceableData) => {
        data.agreementNumber = data.agreementNumber ? data.agreementNumber : 0;
        data.clientId = data.clientId ? data.clientId : 0;
        data.contractId = data.contractId ? data.contractId : 0;
        data.contractLookupId = data.contractLookupId ? data.contractLookupId : 0;
        data.projectId = data.projectId ? data.projectId : 0;
        data.grossAcres = data.grossAcres ? data.grossAcres : 0;
        data.netAcres = data.netAcres ? parseFloat(MathUtil.cleanNumber(data.netAcres, 7)) : 0;
        data.totalBonus = data.bonusAmount && data.netAcres && data.bonusUnits === 'perAcre'
            ? Number(MathUtil.cleanDollarAmount(data.bonusAmount * data.netAcres))
            : data.bonusAmount && data.bonusUnits === 'flatFee'
                ? data.bonusAmount : 0;

        data.contractType = data.contractType ? data.contractType : '';
        data.leaseNumber = data.leaseNumber ? data.leaseNumber : '';
        data.legalDescription = data.legalDescription ? data.legalDescription : '';
        data.lesseeName = data.lesseeName ? data.lesseeName : '';
        data.miscContractNumber = data.miscContractNumber ? data.miscContractNumber : '';
        data.ownerName = data.ownerName ? data.ownerName : '';
        data.bonusUnits = data.bonusUnits ? data.bonusUnits : '';
        // if (data.invoices.length > 1) {
        //     let currentNumber = data.invoices[data.invoices.length - 1].invoiceNumber;
        //     data.latestInvoiceNumber = currentNumber ?
        //         currentNumber.substring(0, data.latestInvoiceNumber.length - 1)
        //         + String.fromCharCode(data.latestInvoiceNumber.charCodeAt(currentNumber.length - 1) + 1) : '';
        // } else if (data.invoices.length > 0) {
        //     data.latestInvoiceNumber = data.invoices[data.invoices.length - 1].invoiceNumber + '-A';
        // } else {
        //     data.latestInvoiceNumber = data.latestInvoiceNumber ? (parseInt(data.latestInvoiceNumber.substring(0, 6), 10) + 1).toString() : '';
        // }
        data.latestInvoiceNumber = data.latestInvoiceNumber ? (parseInt(data.latestInvoiceNumber.substring(0, 6), 10) + 1).toString() : '';

        data.createdDate = data.createdDate
            ? (moment(data.createdDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.inHandDate = data.inHandDate
            ? (moment(data.inHandDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.leaseDate = data.leaseDate
            ? (moment(data.leaseDate).format('YYYY-MM-DD') as unknown as Date)
            : null;

        return data;
    };

    const formatInvoiceData = (data: Invoice) => {
        data.id = currentInvoice?.id ? currentInvoice.id : 0;
        data.invoiceableLookupId = contractNumber ? contractNumber : 0;
        data.invoiceNumber = data.invoiceNumber ? data.invoiceNumber : '';
        data.collectNumber = data.collectNumber ? parseInt(data.collectNumber + '', 10) : 0;
        data.agreementInHandDate = data.agreementInHandDate
            ? (moment(data.agreementInHandDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.draftDueDate = data.draftDueDate
            ? (moment(data.draftDueDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.draftNumber = data.draftNumber ? parseInt(data.draftNumber + '', 10) : 0;
        data.invoiceDueDate = data.invoiceDueDate
            ? (moment(data.invoiceDueDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.checkNumber = data.checkNumber ? parseInt(data.checkNumber + '', 10) : 0;
        data.checkDate = data.checkDate
            ? (moment(data.checkDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.bonusAmount = data.bonusAmount ? parseFloat(data.bonusAmount + '') : 0;
        data.recordingFee = data.recordingFee ? parseFloat(data.recordingFee + '') : 0;
        data.rentAmount = data.rentAmount ? parseFloat(data.rentAmount + '') : 0;
        data.invoiceTotal = data.invoiceTotal ? parseFloat(data.invoiceTotal + '') : 0;
        data.bankCharge = data.bankCharge ? parseFloat(data.bankCharge + '') : 0;
        data.otherFee = data.otherFee ? parseFloat(data.otherFee + '') : 0;
        data.arDate = data.arDate
            ? (moment(data.arDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.paidAmount = data.paidAmount ? parseFloat(data.paidAmount + '') : 0;
        data.datePaid = data.datePaid
            ? (moment(data.datePaid).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.checkSent = data.checkSent
            ? (moment(data.checkSent).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.collectionType = data.collectionType ? data.collectionType : '';
        data.invoiceStatusId = data.invoiceStatusId ? parseInt(data.invoiceStatusId + '', 10) : null;
        data.clientInvoiceNotes = data.clientInvoiceNotes ? data.clientInvoiceNotes : '';
        data.invoiceStatus = data.invoiceStatus ? data.invoiceStatus : '';

        return data;
    };

    const formatNotes = (data: LeaseNotes) => {
        data.id = invoiceData?.contractId > 0 && invoiceData?.contractType === 'Lease' ? invoiceData.contractId : 0;
        data.clientFacingNotes = data.clientFacingNotes ? data.clientFacingNotes : '';
        data.internalNotes = data.internalNotes ? data.internalNotes : '';

        return data;
    };

    const formatCheckData = (data: Check) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.leaseId = invoiceData.contractId ? parseInt(invoiceData.contractId + '', 10) : 0;
        data.companyId = data.companyId ? parseInt(data.companyId + '', 10) : 0;
        data.countyId = data.countyId ? parseInt(data.countyId + '', 10) : 0;
        data.stateId = data.stateId ? parseInt(data.stateId + '', 10) : 0;
        data.projectNumber = data.projectNumber ? parseInt(data.projectNumber + '', 10) : 0;
        data.checkTypeId = data.checkTypeId ? parseInt(data.checkTypeId + '', 10) : 0;

        data.invoiceTotal = data.invoiceTotal ? parseFloat(data.invoiceTotal + '') : 0;
        data.totalAmount = data.totalAmount ? parseFloat(data.totalAmount + '') : 0;
        data.bonusAmount = data.bonusAmount ? parseFloat(data.bonusAmount + '') : 0;
        data.otherAmount = data.otherAmount ? parseFloat(data.otherAmount + '') : 0;
        data.rentalsAmount = data.rentalsAmount ? parseFloat(data.rentalsAmount + '') : 0;
        data.bankFeesAmount = data.bankFeesAmount ? parseFloat(data.bankFeesAmount + '') : 0;
        data.withholdingPercent = data.withholdingPercent ? parseFloat(data.withholdingPercent + '') : 0;
        data.totalRemaining = data.totalRemaining ? parseFloat(data.totalRemaining + '') : 0;

        data.checkNumber = data.checkNumber ? data.checkNumber : '';
        data.voidReason = data.voidReason ? data.voidReason : '';
        data.payee = data.payee ? data.payee : '';
        data.address = data.address ? data.address : '';
        data.stateName = data.stateName ? data.stateName : '';
        data.city = data.city ? data.city : '';
        data.zip = data.zip ? data.zip : '';
        data.memo = data.memo ? data.memo : '';
        data.leaseNumber = data.leaseNumber ? data.leaseNumber : '';
        data.invoiceNumber = data.invoiceNumber ? data.invoiceNumber : '';

        data.checkDate = data.checkDate
            ? (moment(data.checkDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.dateSent = data.dateSent
            ? (moment(data.dateSent).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.glDate = data.glDate
            ? (moment(data.glDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.voidDate = data.voidDate
            ? (moment(data.voidDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.checkDueDate = data.checkDueDate
            ? (moment(data.checkDueDate).format('YYYY-MM-DD') as unknown as Date)
            : null;

        data.isVoid = !!data.isVoid ? data.isVoid : null;

        return data;
    };

    const formatInvoiceGroupData = (data: InvoiceGroup) => {
        let newData: InvoiceGroup = new InvoiceGroup();

        newData.clientId = invoiceData?.clientId ? parseInt(invoiceData?.clientId + '', 10) : 0;
        newData.companyId = data.companyId ? parseInt(data.companyId + '', 10) : 0;
        newData.stateId = data.stateId ? parseInt(data.stateId + '', 10) : 0;

        newData.netAcres = data.netAcres ? parseFloat(data.netAcres + '') : 0;

        newData.groupNumber = data.groupNumber ? data.groupNumber : '';
        newData.clientName = data.clientName ? data.clientName : '';
        newData.clientAddress = data.clientAddress ? data.clientAddress : '';
        newData.salutation = data.salutation ? data.salutation : '';
        newData.signature = data.signature ? data.signature : '';
        newData.signatureTitle = data.signatureTitle ? data.signatureTitle : '';
        newData.landmanName = data.landmanName ? data.landmanName : '';
        newData.city = data.city ? data.city : '';
        newData.zipCode = data.zipCode ? data.zipCode : '';
        newData.letterText = data.letterText ? data.letterText : '';
        newData.bonusOption = data.bonusOption ? data.bonusOption : '';

        newData.isCoverLetter = data.isCoverLetter ? (data.isCoverLetter.toString() === 'true') : null;
        newData.printPageNumber = data.printPageNumber ? (data.printPageNumber.toString() === 'true') : null;
        newData.sectionOnInvoice = data.sectionOnInvoice ? (data.sectionOnInvoice.toString() === 'true') : null;

        newData.groupDate = data.groupDate
            ? (moment(data.groupDate).format('YYYY-MM-DD') as unknown as Date)
            : null;

        newData.invoices = data.invoices;

        return newData;
    };

    console.log('invoice data', invoiceData);

    return (
        <>
            <Page>
                <Page.Section width={12}>
                    <Panel>
                        <div>
                            <RHFLegend title="Contract Details" />
                            <div style={{ display: 'flex' }}>
                                <div className="mr-2" style={{ width: '100%' }}>
                                    <div style={{ display: 'flex' }}>
                                        {(invoiceData?.sectionNumber > 0 || invoiceData?.easternMapNumber > 0) && (
                                            <div style={{ padding: '1rem', width: '40%' }}>
                                                <div style={{ fontSize: '1.6rem' }}>Document #</div>
                                                <div><a onClick={navigateToContract}>{invoiceData?.leaseNumber}</a></div>
                                            </div>
                                        )}
                                        {!(invoiceData?.sectionNumber > 0 || invoiceData?.easternMapNumber > 0) && (
                                            <div style={{ padding: '1rem', width: '40%' }}>
                                                <div style={{ fontSize: '1.6rem' }}>Document #</div>
                                                <div>{invoiceData?.leaseNumber}</div>
                                                <div>Link not available</div>
                                            </div>
                                        )}
                                        <div style={{ padding: '1rem', width: '40%' }}>
                                            <div style={{ fontSize: '1.6rem' }}>Document Type</div>
                                            <div>{invoiceData?.contractType}</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '20%' }}>
                                            <div style={{ fontSize: '1.6rem' }}>Project #</div>
                                            <div>{invoiceData?.projectId}</div>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex' }}>
                                        <div style={{ padding: '1rem', width: '40%' }}>
                                            <div style={{ fontSize: '1.6rem' }}>Landman</div>
                                            <div>N/A</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '40%' }}>
                                            <div style={{ fontSize: '1.6rem' }}>Acres Gross</div>
                                            <div>{invoiceData?.grossAcres}</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '20%' }}>
                                            <div style={{ fontSize: '1.6rem' }}>Acres Net</div>
                                            <div>{invoiceData?.netAcres}</div>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex' }}>
                                        <div style={{ padding: '1rem', width: '40%' }}>
                                            <div style={{ fontSize: '1.6rem' }}>Lessee Name</div>
                                            <div>{invoiceData?.lesseeName}</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '40%' }}>
                                            <div style={{ fontSize: '1.6rem' }}>Lessor Name</div>
                                            <div>{invoiceData?.ownerName}</div>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex' }}>
                                        <div style={{ padding: '1rem', width: '40%' }}>
                                            <div style={{ fontSize: '1.6rem' }}>Created By</div>
                                            <div>N/A</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '40%' }}>
                                            <div style={{ fontSize: '1.6rem' }}>Created Date</div>
                                            <div>{invoiceData?.createdDate ? invoiceData.createdDate : 'N/A'}</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '20%' }}>
                                            <div style={{ fontSize: '1.6rem' }}>In-Hand Date</div>
                                            <div>{invoiceData?.inHandDate}</div>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex' }}>
                                        <div style={{ padding: '1rem', width: '40%' }}>
                                            <div style={{ fontSize: '1.6rem' }}>Modified By</div>
                                            <div>N/A</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '40%' }}>
                                            <div style={{ fontSize: '1.6rem' }}>Modified Date</div>
                                            <div>N/A</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '20%' }}>
                                            <div style={{ fontSize: '1.6rem' }}>Clauses Checked?</div>
                                            <div>No</div>
                                        </div>
                                    </div>
                                    {/* {invoiceData?.contractType === 'Lease' && ( */}
                                        <form onSubmit={handleSubmit(onSubmit)}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ padding: '1rem', width: '100%' }}>
                                                    <RHFTextArea name="clientFacingNotes" label="Client Facing Notes" register={register} errors={errors} />
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ padding: '1rem', width: '100%' }}>
                                                    <RHFTextArea name="internalNotes" label="Internal Notes" register={register} errors={errors} />
                                                </div>
                                            </div>
                                            <div>
                                                <Button text="Save Lease Notes" intent="primary" type="submit" />
                                            </div>
                                        </form>
                                    {/* )} */}
                                </div>
                            </div>
                            <div style={{ display: 'flex' }}>
                                <div className="mr-2" style={{ width: '100%' }}>
                                    <div style={{ padding: '1rem' }}>
                                        <div style={{ textAlign: 'left', fontSize: '1.6rem' }}>Legal Description</div>
                                        <div>{invoiceData?.legalDescription}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <RHFLegend title="Invoices" />
                            <div style={{ display: 'flex' }}>
                                <div className="mr-2" style={{ width: '100%' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <div style={{ padding: '1rem', width: '15%' }}>
                                            <div>Invoice #</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '15%' }}>
                                            <div>Bonus Amount</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '15%' }}>
                                            <div>Rent Amount</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '15%' }}>
                                            <div>Recording Fee</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '15%' }}>
                                            <div>Bank Charge</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '15%' }}>
                                            <div>Other Fee</div>
                                        </div>
                                        <div style={{ padding: '1rem', width: '10%' }}>
                                            <div>Invoice Total</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <CardContainer>
                                {displayInvoices()}
                                <br />
                                {invoiceTotals()}
                            </CardContainer>
                            <div style={{ display: 'flex' }}>
                                <div className="mr-2">
                                    <Button size="small" key={-1} text="Create Invoice" onClick={() => onInvoiceAction('Manage Invoice', -1)} />
                                </div>
                                <div className="mr-2">
                                    <Button size="small" key={-1} text="Manage Invoice Groups" onClick={() => onInvoiceAction('Manage Invoice Groups', -1)} />
                                </div>
                            </div>

                            <AddInvoiceDialog
                                invoice={currentInvoice}
                                invoiceableLookupId={contractNumber}
                                nextInvoiceNumber={invoiceData?.latestInvoiceNumber}
                                clientNumber={invoiceData?.clientId}
                                projectNumber={invoiceData?.projectId}
                                dialogName={DialogList.ADD_INVOICE}
                                totalBonus={invoiceData?.totalBonus}
                                bonusType={invoiceData?.bonusUnits === 'perAcre' ? 'Per Acre' : invoiceData?.bonusUnits === 'flatFee' ? 'Flat Fee' : ''}
                                bonusAmount={invoiceData?.bonusAmount}
                                countyId={invoiceData?.countyId}
                                stateId={invoiceData?.stateId}
                                hideDialog={(isReset: boolean) => hideDialogBox(isReset)}
                                formatData={(data: Invoice) => formatInvoiceData(data)}
                            />
                            <CreateCheckDialog
                                invoice={currentInvoice}
                                leaseNumber={invoiceData?.leaseNumber}
                                leaseId={invoiceData?.contractId}
                                dialogName={DialogList.CREATE_CHECK}
                                clientNumber={invoiceData?.clientNumber}
                                projectNumber={invoiceData?.projectNumber}
                                clientId={invoiceData?.clientId}
                                projectId={invoiceData?.projectId}
                                checkOptions={checkOptions}
                                formatData={(data: Check) => formatCheckData(data)}
                                hideDialog={hideDialogBox}
                            />
                            <ManageChecksDialog
                                dialogName={DialogList.MANAGE_CHECKS}
                                clientId={invoiceData?.clientId}
                                projectId={invoiceData?.projectId}
                                contractId={invoiceData?.contractId}
                                checksList={checksList}
                                checkOptions={checkOptions}
                                formatData={(data: Check) => formatCheckData(data)}
                                hideDialog={hideDialogBox}
                                extendDueDate={(checkNumber: string) => extendDueDate(checkNumber)}
                            />
                            <ManageInvoiceGroupsDialog
                                dialogName={DialogList.MANAGE_INVOICE_GROUPS}
                                clientId={invoiceData?.clientId}
                                projectId={invoiceData?.projectId}
                                invoiceGroups={invoiceGroupsList}
                                companies={checkOptions?.companies}
                                states={checkOptions?.states}
                                formatData={(data: InvoiceGroup) => formatInvoiceGroupData(data)}
                                hideDialog={hideDialogBox}
                            />
                            <ExtendCheckDueDateDialog
                                clientId={invoiceData?.clientId}
                                projectId={invoiceData?.projectId}
                                dialogName={DialogList.EXTEND_CHECK_DUE_DATE}
                                currentCheck={currentCheck}
                                currentInvoice={currentInvoice}
                                hideDialog={hideDialogBox}
                            />
                        </div>
                    </Panel>
                </Page.Section>
            </Page>
        </>
    );
};

export default InvoicingDetail; 



// WEBPACK FOOTER //
// ./src/components/invoicing/pages/InvoicingDetail.tsx